import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { InventoryTopBar } from './InventoryTopBar';
import { DiscoveryInventoryTable } from './DiscoveryInventoryTable';
import { useDiscoveryInventoryData } from '../useDiscoveryInventoryData';
import { WarningModal } from '../../../../components';
import { hasValidIP } from '../../helpers';
import { MARK_READY_WARNING } from '../../../../constants/discovery';
import {
    useGetDiscoveryMoveGroupsQuery,
    useCreateMoveGroupMutation,
    useAssignVMsToMoveGroupOrApplicationMutation,
} from '../../../../api/discovery';
import { useNotificationContext } from '../../../../providers/NotificationProvider';

const DEFAULT_INITIAL_FILTERS = [];

export const DiscoveryInventoryContainer = ({
    onRowClick,
    selectedRowData,
    moveGroupsAndApps = false,
    onOpenManageModal,
    onSelectionChange,
    onInventoryDataUpdate,
    onTotalElementsChange,
    unassignedOnly = false,
    refetchUnassignedInventoryRef = null,
    initialFilters = DEFAULT_INITIAL_FILTERS,
}) => {
    const orgId = useMemo(() => sessionStorage.getItem('userOrg'), []);

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    // Keep search value state here so we can display it in the input
    // but debounce the actual data fetching in the hook
    const [searchValue, setSearchValue] = useState('');

    const {
        inventoryTableData,
        inventoryLoading,
        inventoryFetching,
        handleColumnSelection,
        selectedColumns,
        pagination,
        setPagination,
        handlePaginationChange,
        handleFilterDiscoveryInventoryData,
        filterValue,
        resetFilters,
        refetchInventoryData,
        selectedFilters,
        setSelectedFilters,
        handleChangeStatus,
        handleSendToMigration,
        handleSortChange,
        sortField,
        sortOrder,
        totalElements,
    } = useDiscoveryInventoryData({ orgId, searchValue, unassignedOnly, initialFilters });

    // Notify parent of total elements count
    useEffect(() => {
        onTotalElementsChange?.(totalElements);
    }, [totalElements, onTotalElementsChange]);

    // Store refetch function in ref for unassigned inventory
    useEffect(() => {
        if (unassignedOnly && refetchUnassignedInventoryRef) {
            // eslint-disable-next-line react-hooks/immutability
            refetchUnassignedInventoryRef.current = refetchInventoryData;
        }
    }, [unassignedOnly, refetchUnassignedInventoryRef, refetchInventoryData]);

    const [showWarningModal, setShowWarningModal] = useState(false);
    const [warningModalBody, setWarningModalBody] = useState('');
    const [showSendToMigrationModal, setShowSendToMigrationModal] = useState(false);

    const { notifySuccess, notifyError } = useNotificationContext();

    const { data: moveGroupsData, refetch: refetchMoveGroups } = useGetDiscoveryMoveGroupsQuery(orgId, {
        skip: !orgId,
        refetchOnMountOrArgChange: true,
    });

    const [createMoveGroup] = useCreateMoveGroupMutation();
    const [assignVMsToMoveGroupOrApplication] = useAssignVMsToMoveGroupOrApplicationMutation();

    // Map move groups to options for select input
    const moveGroupOptions = useMemo(() => {
        if (!moveGroupsData) return [];
        const content = moveGroupsData.content || moveGroupsData || [];
        return content.map((group) => ({
            value: group.id,
            label: group.name,
        }));
    }, [moveGroupsData]);

    const handleSelectionChange = useCallback(
        (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
            onSelectionChange?.(keys);
        },
        [onSelectionChange],
    );

    // Check if any selected VMs are missing a move group
    const hasVMsWithoutMoveGroup = useMemo(() => {
        return selectedRows.some((row) => !row.move_group_id);
    }, [selectedRows]);

    // handle changing ready status with ip validation
    // Use provided rows if available, otherwise use selectedRows from state
    const handleChangeReadyStatus = useCallback(
        (vmIds, status, rows = null) => {
            const rowsToCheck = rows || selectedRows;
            if (status === 'ready' && rowsToCheck.some((row) => !hasValidIP(row.ip_addresses))) {
                setWarningModalBody(MARK_READY_WARNING);
                setShowWarningModal(true);
                return;
            }
            handleChangeStatus(vmIds, status);
        },
        [handleChangeStatus, selectedRows],
    );

    // Notify parent when inventory data updates (so drawer can refresh)
    useEffect(() => {
        if (onInventoryDataUpdate && inventoryTableData) {
            onInventoryDataUpdate(inventoryTableData);
        }
    }, [inventoryTableData, onInventoryDataUpdate]);

    // Handle sending to migration with optional move group assignment
    const handleSendToMigrationWithAssignment = useCallback(
        async (vmIds, assignmentData) => {
            try {
                if (assignmentData?.assignToMoveGroup && assignmentData?.moveGroupValue) {
                    const moveGroupValue = assignmentData.moveGroupValue;

                    const vmsWithoutMoveGroup = selectedRows
                        .filter((row) => vmIds.includes(row.id) && !row.move_group_id)
                        .map((row) => row.id);

                    if (vmsWithoutMoveGroup.length > 0) {
                        const numericValue =
                            typeof moveGroupValue === 'string' ? parseInt(moveGroupValue, 10) : moveGroupValue;
                        const existingGroup = moveGroupOptions.find(
                            (group) => group.value === numericValue || group.value === moveGroupValue,
                        );

                        let moveGroupId;

                        if (existingGroup) {
                            moveGroupId = existingGroup.value;
                        } else {
                            const createResult = await createMoveGroup({
                                organizationId: orgId,
                                name: moveGroupValue,
                                application_ids: [],
                            }).unwrap();
                            moveGroupId = createResult.id;
                            notifySuccess({ message: `Move group "${moveGroupValue}" created successfully.` });
                            refetchMoveGroups();
                        }

                        await assignVMsToMoveGroupOrApplication({
                            orgId,
                            vmIds: vmsWithoutMoveGroup,
                            moveGroupId,
                        }).unwrap();
                        notifySuccess({ message: 'VMs assigned to move group successfully.' });
                    }
                }
                await handleSendToMigration(vmIds);
            } catch {
                notifyError({ message: 'Failed to process move group assignment.' });
            }
        },
        [
            moveGroupOptions,
            createMoveGroup,
            assignVMsToMoveGroupOrApplication,
            handleSendToMigration,
            orgId,
            notifySuccess,
            notifyError,
            refetchMoveGroups,
            selectedRows,
        ],
    );

    return (
        <div>
            <InventoryTopBar
                moveGroupsAndApps={moveGroupsAndApps}
                handleColumnSelection={handleColumnSelection}
                selectedColumns={selectedColumns}
                selectedRowKeys={selectedRowKeys}
                selectedRows={selectedRows}
                handleFilterDiscoveryInventoryData={handleFilterDiscoveryInventoryData}
                filterValue={filterValue}
                resetFilters={resetFilters}
                refetchInventoryData={refetchInventoryData}
                setSearchValue={setSearchValue}
                searchValue={searchValue}
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
                handleChangeStatus={handleChangeStatus}
                onOpenManageModal={onOpenManageModal}
                setShowSendToMigrationModal={setShowSendToMigrationModal}
            />
            <DiscoveryInventoryTable
                selectedColumns={selectedColumns}
                isLoading={inventoryLoading}
                isFetching={inventoryFetching}
                tableData={inventoryTableData}
                pagination={pagination}
                setPagination={setPagination}
                handlePaginationChange={handlePaginationChange}
                onRowClick={onRowClick}
                selectedRowData={selectedRowData}
                onSelectionChange={handleSelectionChange}
                handleChangeReadyStatus={handleChangeReadyStatus}
                handleSendToMigration={handleSendToMigrationWithAssignment}
                onSortChange={handleSortChange}
                sortField={sortField}
                sortOrder={sortOrder}
                showSendToMigrationModal={showSendToMigrationModal}
                setShowSendToMigrationModal={setShowSendToMigrationModal}
                moveGroups={moveGroupOptions}
                hasVMsWithoutMoveGroup={hasVMsWithoutMoveGroup}
                enableRowDragging={moveGroupsAndApps}
            />
            <WarningModal open={showWarningModal} onOk={() => setShowWarningModal(false)}>
                {warningModalBody}
            </WarningModal>
        </div>
    );
};
